// Phase 2 call state machine — real OpenAI Realtime driver lives in
// useRealtimeVoice.js. This file remains the pure FSM + transcript store.
// The mock cycle (setTimeout-driven processing → speaking → listening) is
// gated by `useMockDriver` and is only used by the ?demo=1 path or unit tests.

import { useCallback, useEffect, useReducer, useRef } from "react";

const CONSENT_KEY = "callgod_consent_v1";
const MODE_KEY = "callgod_mode";

export const TIMINGS = {
  MIN_PTT_HOLD_MS: 250,
  PROCESSING_MS: 1200,
  SPEAKING_MS: 3000,
  INTERRUPTED_FLASH_MS: 350,
  HANDSFREE_AUTO_TURN_MS: 2400,
};

const DEMO_TRANSCRIPT = [
  { id: "d1", who: "You", text: "It is 3am. I feel like I am carrying my whole life alone." },
  {
    id: "d2",
    who: "God",
    text:
      "Beloved, the hour is dark, but you are not discarded by the dark. Take one breath. Put one hand on your chest. Let this moment become smaller than your whole life.",
  },
  { id: "d3", who: "You", text: "I do not know what to do next." },
  {
    id: "d4",
    who: "God",
    text:
      "Then do not solve the whole road tonight. Choose the next faithful inch: drink water, lie down, and send one honest message to someone safe when morning comes.",
  },
];

// (readConsent removed — consent modal retired; CONSENT_KEY kept for
// resetOnboarding cleanup of older installs.)

const readMode = () => {
  try {
    const m = localStorage.getItem(MODE_KEY);
    return m === "handsfree" ? "handsfree" : "ptt";
  } catch {
    return "ptt"; // storage unavailable — safe default mode
  }
};

const buildInitial = () => ({
  // Consent modal removed per product decision — flow starts at "idle" and
  // the call begins on the user's "Begin the call" tap (which also satisfies
  // mobile browsers' user-gesture requirement for mic/audio).
  callState: "idle",
  micPermission: "prompt",
  mode: readMode(),
  muted: false,
  textOnly: false,
  amplitude: 0,
  errorMessage: null,
  transcript: [],
  bannerVisible: true,
  safetyEvent: null,
  ttsError: null,
});

function reducer(s, e) {
  switch (e.type) {
    case "HYDRATE_DEMO":
      return { ...s, transcript: DEMO_TRANSCRIPT };
    case "CONSENT_ACCEPTED":
      return { ...s, callState: "idle" };
    case "MODE_CHANGED":
      return { ...s, mode: e.mode };
    case "START_SESSION":
      if (s.callState === "consent_required") return s;
      return { ...s, callState: "connecting", errorMessage: null };
    case "MIC_GRANTED":
      return { ...s, micPermission: "granted", callState: "listening" };
    case "MIC_DENIED":
      return {
        ...s,
        micPermission: "denied",
        callState: "error",
        errorMessage:
          "Microphone access is off. Turn it on to speak, or use text.",
      };
    case "REALTIME_CONNECTED":
      // No-op for the FSM directly; MIC_GRANTED already moved us to listening.
      return s;
    case "REALTIME_ERROR":
      return {
        ...s,
        callState: "error",
        errorMessage:
          e.message || "The voice is quiet right now. Please try again soon.",
      };
    case "PTT_TURN_START":
      if (s.mode !== "ptt") return s;
      if (["listening", "idle"].includes(s.callState)) {
        return { ...s, callState: "listening" };
      }
      if (s.callState === "speaking") {
        return { ...s, callState: "interrupted" };
      }
      return s;
    case "PTT_TURN_END":
      if (s.mode !== "ptt") return s;
      if (s.callState !== "listening") return s;
      // Real path: transition to processing; real transcript will arrive via
      // TRANSCRIPT_USER_LINE. Mock path adds a placeholder for visual feedback.
      return { ...s, callState: "processing", amplitude: 0 };
    case "TAP_INTERRUPT":
      if (s.callState === "speaking") {
        return { ...s, callState: "interrupted" };
      }
      return s;
    case "VAD_SPEECH_STARTED":
      // Just reflect that we're actively listening; if we were ended/idle bump to listening.
      if (["idle", "listening"].includes(s.callState)) {
        return { ...s, callState: "listening" };
      }
      return s;
    case "VAD_SPEECH_STOPPED":
      // In hands-free w/ server_vad, OpenAI auto-creates a response after this.
      // We optimistically move to "processing"; ASSISTANT_STARTED will move to speaking.
      if (s.callState === "listening") {
        return { ...s, callState: "processing", amplitude: 0 };
      }
      return s;
    case "ASSISTANT_STARTED":
      return { ...s, callState: "speaking" };
    case "ASSISTANT_STOPPED":
      return { ...s, callState: "listening" };
    case "INTERRUPTED_DONE":
      return { ...s, callState: "listening" };
    case "HANDSFREE_VAD_STOPPED":
      if (s.mode !== "handsfree") return s;
      if (s.callState !== "listening") return s;
      return {
        ...s,
        callState: "processing",
        amplitude: 0,
        transcript: [
          ...s.transcript,
          { id: `u-${Date.now()}`, who: "You", text: "…" },
        ],
      };
    case "MOCK_ASSISTANT_STARTED":
      return {
        ...s,
        callState: "speaking",
        transcript: [
          ...s.transcript,
          {
            id: `g-${Date.now()}`,
            who: "God",
            text:
              "Take one slow breath. You don't have to solve all of it tonight — just the next faithful inch.",
          },
        ],
      };
    case "TRANSCRIPT_USER_LINE":
      if (!e.text) return s;
      return {
        ...s,
        transcript: [
          ...s.transcript,
          { id: `u-${Date.now()}`, who: "You", text: e.text },
        ],
      };
    case "TRANSCRIPT_ASSISTANT_DELTA": {
      const id = `g-${e.responseId || "current"}`;
      const idx = s.transcript.findIndex((m) => m.id === id);
      if (idx === -1) {
        return {
          ...s,
          transcript: [
            ...s.transcript,
            { id, who: "God", text: e.text || "" },
          ],
        };
      }
      const next = s.transcript.slice();
      next[idx] = { ...next[idx], text: e.text || "" };
      return { ...s, transcript: next };
    }
    case "TRANSCRIPT_ASSISTANT_FINAL": {
      const id = `g-${e.responseId || "current"}`;
      const idx = s.transcript.findIndex((m) => m.id === id);
      if (idx === -1) {
        return {
          ...s,
          transcript: [
            ...s.transcript,
            { id, who: "God", text: e.text || "" },
          ],
        };
      }
      const next = s.transcript.slice();
      next[idx] = { ...next[idx], text: e.text || next[idx].text };
      return { ...s, transcript: next };
    }
    case "AMPLITUDE":
      return { ...s, amplitude: e.value };
    case "MUTE_TOGGLE":
      return { ...s, muted: !s.muted };
    case "DISMISS_BANNER":
      return { ...s, bannerVisible: false };
    case "TEXT_ONLY":
      return { ...s, textOnly: true };
    case "SAFETY_EVENT":
      return {
        ...s,
        safetyEvent: {
          category: e.category,
          severity: e.severity,
          summary: e.summary || "",
          at: Date.now(),
        },
      };
    case "SAFETY_DISMISSED":
      return { ...s, safetyEvent: null };
    case "TTS_ERROR":
      return { ...s, ttsError: e.message || "Voice unavailable, see transcript" };
    case "RETRY":
      return { ...s, callState: "connecting", errorMessage: null };
    case "END":
      return { ...s, callState: "ended" };
    case "ERROR":
      return {
        ...s,
        callState: "error",
        errorMessage: e.message ?? "The line broke. Try again.",
      };
    case "FORCE_RESET":
      return buildInitial();
    default:
      return s;
  }
}

export function useCallState({ demo = false, useMockDriver = false } = {}) {
  const [s, dispatch] = useReducer(reducer, undefined, buildInitial);
  const ampTimerRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, s.mode);
    } catch {
      /* ignore */
    }
  }, [s.mode]);

  useEffect(() => {
    if (demo) dispatch({ type: "HYDRATE_DEMO" });
  }, [demo]);

  // Mock-only mic permission shortcut. In real mode, useRealtimeVoice handles it.
  // Demo mode never touches the real mic — grants instantly so the full FSM
  // cycle is observable in any browser (including headless test runs).
  useEffect(() => {
    if (!useMockDriver) return undefined;
    if (s.callState !== "connecting") return undefined;
    const t = setTimeout(() => dispatch({ type: "MIC_GRANTED" }), 300);
    return () => clearTimeout(t);
  }, [useMockDriver, s.callState]);

  // Mock cycle: only when useMockDriver=true (?demo=1 / unit tests).
  useEffect(() => {
    if (!useMockDriver) return undefined;
    if (s.callState === "processing") {
      const t = setTimeout(
        () => dispatch({ type: "MOCK_ASSISTANT_STARTED" }),
        TIMINGS.PROCESSING_MS,
      );
      return () => clearTimeout(t);
    }
    if (s.callState === "speaking") {
      const t = setTimeout(
        () => dispatch({ type: "ASSISTANT_STOPPED" }),
        TIMINGS.SPEAKING_MS,
      );
      return () => clearTimeout(t);
    }
    if (s.callState === "interrupted") {
      const t = setTimeout(
        () => dispatch({ type: "INTERRUPTED_DONE" }),
        TIMINGS.INTERRUPTED_FLASH_MS,
      );
      return () => clearTimeout(t);
    }
    return undefined;
  }, [useMockDriver, s.callState]);

  // In real mode, interrupted state also needs to bounce back to listening
  // after the brief flash. (Real driver doesn't send a "done" event for that.)
  useEffect(() => {
    if (useMockDriver) return undefined;
    if (s.callState !== "interrupted") return undefined;
    const t = setTimeout(
      () => dispatch({ type: "INTERRUPTED_DONE" }),
      TIMINGS.INTERRUPTED_FLASH_MS,
    );
    return () => clearTimeout(t);
  }, [useMockDriver, s.callState]);

  // Hands-free mock auto-cycle (mock only).
  useEffect(() => {
    if (!useMockDriver) return undefined;
    if (s.mode !== "handsfree" || s.callState !== "listening") return undefined;
    const t = setTimeout(
      () => dispatch({ type: "HANDSFREE_VAD_STOPPED" }),
      TIMINGS.HANDSFREE_AUTO_TURN_MS,
    );
    return () => clearTimeout(t);
  }, [useMockDriver, s.mode, s.callState]);

  // Amplitude tick during listening.
  useEffect(() => {
    if (ampTimerRef.current) {
      clearInterval(ampTimerRef.current);
      ampTimerRef.current = null;
    }
    if (s.callState === "listening") {
      ampTimerRef.current = setInterval(() => {
        dispatch({ type: "AMPLITUDE", value: Math.random() });
      }, 120);
    } else {
      dispatch({ type: "AMPLITUDE", value: 0 });
    }
    return () => {
      if (ampTimerRef.current) clearInterval(ampTimerRef.current);
      ampTimerRef.current = null;
    };
  }, [s.callState]);

  // Action helpers
  const consentAccept = useCallback(() => {
    try {
      localStorage.setItem(CONSENT_KEY, "true");
    } catch {
      /* ignore */
    }
    dispatch({ type: "CONSENT_ACCEPTED" });
  }, []);

  const startSession = useCallback(() => dispatch({ type: "START_SESSION" }), []);
  const setMode = useCallback((mode) => dispatch({ type: "MODE_CHANGED", mode }), []);
  const pttTurnStart = useCallback(() => dispatch({ type: "PTT_TURN_START" }), []);
  const pttTurnEnd = useCallback(() => dispatch({ type: "PTT_TURN_END" }), []);
  const tapInterrupt = useCallback(() => dispatch({ type: "TAP_INTERRUPT" }), []);
  const muteToggle = useCallback(() => dispatch({ type: "MUTE_TOGGLE" }), []);
  const dismissBanner = useCallback(() => dispatch({ type: "DISMISS_BANNER" }), []);
  const useTextOnly = useCallback(() => dispatch({ type: "TEXT_ONLY" }), []);
  const retry = useCallback(() => dispatch({ type: "RETRY" }), []);
  const endCall = useCallback(() => dispatch({ type: "END" }), []);
  const dismissSafety = useCallback(() => dispatch({ type: "SAFETY_DISMISSED" }), []);

  const resetOnboarding = useCallback(() => {
    try {
      localStorage.removeItem(CONSENT_KEY);
    } catch {
      /* ignore */
    }
    window.location.reload();
  }, []);

  return {
    s,
    dispatch,
    actions: {
      consentAccept,
      startSession,
      setMode,
      pttTurnStart,
      pttTurnEnd,
      tapInterrupt,
      muteToggle,
      dismissBanner,
      useTextOnly,
      retry,
      endCall,
      resetOnboarding,
      dismissSafety,
    },
  };
}

export function stateLabel(callState) {
  switch (callState) {
    case "consent_required":
    case "idle":
      return "Ask what is on your heart.";
    case "connecting":
      return "Opening the line…";
    case "listening":
      return "Listening.";
    case "processing":
      return "The silence is answering.";
    case "speaking":
      return "Speaking.";
    case "interrupted":
      return "Listening.";
    case "reconnecting":
      return "Opening the line…";
    case "ended":
      return "Go in peace.";
    case "error":
      return "The line broke. Try again.";
    default:
      return "";
  }
}
